import { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UploadFileProgress } from 'modules/files';
import { uploadFiles } from 'api/apiFileNavigator';
import { useToast } from 'context/ToastContext';
import conf from 'conf/conf';
import spinnerTitle from 'components/others/spinnerTitle';
import LeadEventAttachFile from './LeadEventAttachFile';

interface LeadEventAttachProps {
    newEvent: any;
    setNewEvent: React.Dispatch<React.SetStateAction<any>>;
    disabled?: boolean;
    progress: number | boolean;
    setProgress: (progress: number | boolean) => void;
}

const LeadEventAttach = ({ newEvent, setNewEvent, disabled = false, progress, setProgress }: LeadEventAttachProps) => {
    const { t } = useTranslation(['Leads', 'FileNavigator', 'common']);
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement | null>(null);

    const handleUploadProgress = (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
    };

    const handleChangeFile = async () => {
        const files = refInputFile?.current?.files;
        if (!files) return;
        const uploadData = {
            files: files,
            currentPath: `/leads/${newEvent.leadId}/${Date.now()}`,
            onUploadProgress: handleUploadProgress,
        };

        setProgress(true);
        const resUpload = await uploadFiles(uploadData as any);
        setProgress(false);

        if (!resUpload) return showToast(t('common:messError'), 'danger');
        const aHref = Object.values(uploadData?.files).map(
            (file: any) => `${conf.dataServer}${uploadData.currentPath}/${file.name}`,
        );
        if (aHref)
            setNewEvent((prev: any) => ({
                ...prev,
                eventAttach: prev?.eventAttach ? `${prev.eventAttach};${aHref.join(';')}` : aHref.join(';'),
            }));

        showToast(t('FileNavigator:messDownloadSuccess'), 'success');
    };

    const handleClickDelAttach = (delIndex: number) => {
        const eventAttach = newEvent?.eventAttach
            ?.split(';')
            .filter((_: string, i: number) => i !== delIndex)
            .join(';');

        setNewEvent((prev: any) => ({
            ...prev,
            eventAttach: eventAttach,
        }));
    };

    return (
        <div className="mb-3">
            <input type="file" ref={refInputFile} onChange={handleChangeFile} className="d-none" multiple />

            <label className="form-label text-muted">{t('leadStatus.labelAttach')}</label>
            <div className="form-control">
                {newEvent?.eventAttach && (
                    <ul className="list-inline">
                        {newEvent.eventAttach?.split(';').map((attach: string, index: number) => (
                            <LeadEventAttachFile
                                key={attach}
                                fileName={attach}
                                fileIndex={index}
                                disabled={disabled}
                                delOnClick={handleClickDelAttach}
                            />
                        ))}
                    </ul>
                )}
                <Button
                    variant="light-secondary"
                    size="sm"
                    style={{ height: '34px' }}
                    disabled={disabled || progress !== false}
                    onClick={() => {
                        if (!refInputFile.current) return;
                        refInputFile.current.value = '';
                        refInputFile.current.click();
                    }}
                >
                    {spinnerTitle(
                        <>
                            <i className="bi bi-paperclip fs-16 me-2"></i>
                            {t('leadStatus.btnAttach')}
                        </>,
                        progress !== false,
                        t('buttons.saving'),
                    )}
                </Button>

                {progress !== false && (
                    <div className="pt-2">
                        <UploadFileProgress progress={Number(progress)} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadEventAttach;
